import { Gap, P, theme } from '@salecycle/ui-component-library';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TemplateEditorTabs,
  TEMPLATE_EDITOR_DESKTOP_SIZE_STORAGE_ID,
  TEMPLATE_EDITOR_MOBILE_SIZE_STORAGE_ID,
  TEMPLATE_EDITOR_SPLIT_CONTAINER_CSS_ID,
} from '../constants';
import { useTemplateEditorDispatch, useTemplateEditorState } from '../context/UseTemplateEditorContext';
import { TooltipButton } from './common/TooltipButton';
import { SideBarTabIcon } from './editor-side-bar/SideBarTabIcon';
import { SideBarValidationTabIcon } from './editor-side-bar/SideBarValidationTabIcon';
import { BottomIconSection, PanelIconSection, SectionBreak, TabsBar } from './style';

const toolbarWidth = 55;

export const TemplatePreviewSideTabBar = () => {
  const { t } = useTranslation(['designs', 'trackingLinks']);
  const { activeLeftTab, activeRightTab, validationCounts, rightWindowPercentage, template } = useTemplateEditorState();
  const dispatch = useTemplateEditorDispatch();
  const rightWindowPx = useRef(0);
  const storedDesktopSize = localStorage.getItem(TEMPLATE_EDITOR_DESKTOP_SIZE_STORAGE_ID) ?? '910';
  const storedMobileSize = localStorage.getItem(TEMPLATE_EDITOR_MOBILE_SIZE_STORAGE_ID) ?? '320';
  useEffect(() => {
    const splitContainerWidth = document.getElementById(TEMPLATE_EDITOR_SPLIT_CONTAINER_CSS_ID)?.offsetWidth;
    const rightPX = floatToInt((rightWindowPercentage / 100) * splitContainerWidth);
    rightWindowPx.current = rightPX - toolbarWidth;
  }, [rightWindowPercentage]);

  function floatToInt(value) {
    return value | 0;
  }
  const isActive = (tabName: string) => {
    return activeLeftTab === tabName || activeRightTab === tabName;
  };

  const onTabClick = (tabName: string) => {
    dispatch({
      type: 'SET_RIGHT_WINDOW',
      payload: {
        tab: tabName,
      },
    });
  };

  // Desktop/Mobile view size is defined in pixels, split requires as percent of its container.
  const convertPxToPercent = (viewSizeInPixels: number) => {
    const splitContainerWidth = document.getElementById(TEMPLATE_EDITOR_SPLIT_CONTAINER_CSS_ID)?.offsetWidth;
    return (viewSizeInPixels / splitContainerWidth) * 100;
  };

  const onSizeClick = (value: number) => {
    dispatch({
      type: 'SET_RIGHT_WINDOW_PERCENTAGE',
      payload: {
        percentage: convertPxToPercent(value + toolbarWidth),
      },
    });
  };

  return (
    <TabsBar>
      <PanelIconSection>
        <SideBarTabIcon
          icon="eye"
          active={isActive(TemplateEditorTabs.templatePreview)}
          onClick={() => onTabClick(TemplateEditorTabs.templatePreview)}
          tooltipHeading={t('designs:designGallery.templateEditor.tabBar.preview')}
          tooltipDescription={t('designs:designGallery.templateEditor.tabBar.previewDescription')}
          data-testid="TemplatePreviewIcon"
        />
        <SideBarTabIcon
          icon="code"
          active={isActive(TemplateEditorTabs.templateOutput)}
          onClick={() => onTabClick(TemplateEditorTabs.templateOutput)}
          tooltipHeading={t('designs:designGallery.templateEditor.tabBar.output')}
          tooltipDescription={t('designs:designGallery.templateEditor.tabBar.outputDescription')}
          data-testid="TemplateOutputIcon"
        />
        {template.channel !== 'onsite' && (
          <>
            <SideBarTabIcon
              icon="link"
              active={isActive(TemplateEditorTabs.linkOutput)}
              onClick={() => onTabClick(TemplateEditorTabs.linkOutput)}
              tooltipHeading={t('designs:designGallery.templateEditor.tabBar.linksOutput')}
              tooltipDescription={t('designs:designGallery.templateEditor.tabBar.linksOutputDescription')}
              data-testid="LinkOutputIcon"
            />
            <SideBarValidationTabIcon
              icon="clipboard-list-check"
              active={isActive(TemplateEditorTabs.codeValidation)}
              onClick={() => onTabClick(TemplateEditorTabs.codeValidation)}
              tooltipHeading={t('designs:designGallery.templateEditor.tabBar.validation')}
              tooltipDescription={t('designs:designGallery.templateEditor.tabBar.validationDescription')}
              validationFail={validationCounts?.fail ?? 0}
              validationPass={validationCounts?.pass ?? 0}
              validationWarning={validationCounts?.warning ?? 0}
              data-testid="CodeValidationIcon"
            />
          </>
        )}
        <Gap bottom={5} />
      </PanelIconSection>
      <BottomIconSection>
        {rightWindowPx.current && (
          <P color={theme.vars.newColor.darkGS2}>
            {rightWindowPx.current} {t('designs:designGallery.templateEditor.tabBar.px')}
          </P>
        )}
        <SectionBreak />
        <TooltipButton
          icon="desktop"
          tooltipHeading={t('designs:designGallery.templateEditor.tabBar.desktopView')}
          tooltipDescription={t('designs:designGallery.templateEditor.tabBar.desktopViewDescription')}
          tooltipPosition="right"
          onClick={() => onSizeClick(Number(storedDesktopSize))}
          data-testid="DesktopSizeIcon"
        />
        <TooltipButton
          icon="mobile"
          tooltipHeading={t('designs:designGallery.templateEditor.tabBar.mobileView')}
          tooltipDescription={t('designs:designGallery.templateEditor.tabBar.mobileViewDescription')}
          tooltipPosition="right"
          onClick={() => onSizeClick(Number(storedMobileSize))}
          data-testid="MobileIcon"
        />
        <TooltipButton
          icon="external-link-square-alt"
          tooltipHeading={t('designs:designGallery.templateEditor.tabBar.preview')}
          tooltipDescription={t('designs:designGallery.templateEditor.tabBar.previewDescription')}
          tooltipPosition="right"
          openBlankTab={true}
          data-testid="ExternalPreviewIcon"
        />
      </BottomIconSection>
    </TabsBar>
  );
};
